import type { SOPStatus, SOPApprovalStatus } from '@/types';

export const SOP_STATUS_CONFIG = {
	draft: {
		label: 'Draft',
		textColor: 'text-blue-500',
		bgColor: 'bg-blue-50',
		borderColor: 'border-blue-100'
	},
	published: {
		label: 'Published',
		textColor: 'text-green-500',
		bgColor: 'bg-green-50',
		borderColor: 'border-green-100'
	},
	rejected: {
		label: 'Rejected',
		textColor: 'text-red-500',
		bgColor: 'bg-red-50',
		borderColor: 'border-red-100'
	},
	deprecated: {
		label: 'Deprecated',
		textColor: 'text-amber-700',
		bgColor: 'bg-amber-50',
		borderColor: 'border-amber-200'
	}
} as const;

export const SOP_APPROVAL_STATUS_CONFIG = {
	pending: {
		label: 'Draft',
		textColor: 'text-blue-500',
		bgColor: 'bg-blue-50',
		borderColor: 'border-blue-100'
	},
	approved: {
		label: 'Approved',
		textColor: 'text-green-600',
		bgColor: 'bg-green-50',
		borderColor: 'border-green-100'
	},
	rejected: {
		label: 'Rejected',
		textColor: 'text-red-600',
		bgColor: 'bg-red-50',
		borderColor: 'border-red-100'
	}
} as const;

// The ONLY "SOP Status" shown anywhere a SOP appears (Sources > SOP's tab, Training > SOPs,
// the SOP detail page header) — exactly 3 states: Draft, Approved, Rejected. There is no
// user-facing "Published"/"Approval pending" — `status` (draft/published/rejected/deprecated)
// is an internal content-lifecycle field that predates the approval workflow and must never
// be read directly for display, or it drifts from what the list shows for the same SOP.
export function getSopStatusDisplay(sop: { status: SOPStatus; approvalStatus: SOPApprovalStatus }) {
	if (sop.approvalStatus === 'approved') return SOP_APPROVAL_STATUS_CONFIG.approved;
	if (sop.approvalStatus === 'rejected' || sop.status === 'rejected' || sop.status === 'deprecated') {
		return SOP_APPROVAL_STATUS_CONFIG.rejected;
	}
	return SOP_APPROVAL_STATUS_CONFIG.pending;
}

/** True when customers are still served an older revision than the one on screen. */
export function hasUnpublishedSopDraft(detail: {
	draftPreviewUrl?: string;
	sop?: { hasOpenDraft?: boolean } | null;
}): boolean {
	return Boolean(detail.draftPreviewUrl) || Boolean(detail.sop?.hasOpenDraft);
}

/**
 * Publish is the missing step after saving an already-approved SOP: editing no longer
 * un-approves it, so Save only writes a draft. Offer it while editing (save + publish)
 * and after a draft exists (publish the saved draft).
 */
export function shouldOfferSopPublish(opts: {
	approvalStatus: string;
	isEditing: boolean;
	hasUnpublishedChanges: boolean;
}): boolean {
	if (opts.approvalStatus !== 'approved') return false;
	return opts.isEditing || opts.hasUnpublishedChanges;
}

/** After leaving the editor, keep showing the draft just saved — not the stale published body. */
export function sopEditorDisplayMarkdown(opts: {
	isEditing: boolean;
	draftMarkdown: string;
	loadedMarkdown: string;
	hasUnpublishedChanges: boolean;
}): string {
	if (opts.isEditing) return opts.draftMarkdown;
	if (opts.hasUnpublishedChanges && opts.draftMarkdown) return opts.draftMarkdown;
	return opts.loadedMarkdown;
}
